export function convertQuillToEmailHtml(html) {
    if (!html) return '';

    const tagStyles = {
        H1: 'font-size:28px;font-weight:500;margin:0;line-height:1.5;',
        H2: 'font-size:26px;font-weight:500;margin:0;line-height:1.5;',
        H3: 'font-size:24px;font-weight:500;margin:0;line-height:1.5;',
        H4: 'font-size:22px;font-weight:500;margin:0;line-height:1.5;',
        P: 'font-size:16px;line-height:1.5;margin:0; font-weight:400;',
        A: 'color:#1852D8;text-decoration:none;margin:0;border-bottom: 1px solid #1852D8;',
        STRONG: 'font-weight:bold;',
        B: 'font-weight:bold;',
        EM: 'font-style:italic;',
        I: 'font-style:italic;',
        LI: 'font-size:16px;line-height:1.5;margin:0;',
    };

    try {
        if (typeof window !== 'undefined' && window.DOMParser) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const container = doc.body;

            // Remove unwanted tags
            container.querySelectorAll('script, style, img').forEach(el => el.remove());

            // Handle Lists - Transform to styled paragraphs for better email support
            container.querySelectorAll('ul, ol').forEach(list => {
                const isOrdered = list.tagName === 'OL';
                const fragment = doc.createDocumentFragment();

                list.querySelectorAll('li').forEach((li, index) => {
                    const p = doc.createElement('p');

                    // Apply base LI/P style
                    p.style.cssText = tagStyles.P;

                    // Handle indent
                    const indent = li.className.match(/ql-indent-(\d+)/);
                    if (indent) {
                        p.style.paddingLeft = `${indent[1] * 6}px`;
                    }

                    const prefix = isOrdered ? `${index + 1}. ` : '• ';
                    // Use innerHTML to preserve bold/italic/etc inside LI
                    p.innerHTML = prefix + li.innerHTML;

                    fragment.appendChild(p);
                });

                list.replaceWith(fragment);
            });

            // Universal style application
            container.querySelectorAll('*').forEach(el => {
                const tagName = el.tagName.toUpperCase();
                const style = el.style;

                // 1. Base Tag Styles
                if (tagStyles[tagName]) {
                    const baseStyle = tagStyles[tagName];
                    // Append only if not already having that property to avoid overwriting specific styles
                    el.style.cssText = baseStyle + el.style.cssText;
                }

                // 2. Quill Classes to Inline Styles
                const cls = el.className || '';
                if (typeof cls === 'string') {
                    if (cls.includes('ql-size-small')) style.fontSize = '14px';
                    if (cls.includes('ql-size-large')) style.fontSize = '18px';
                    if (cls.includes('ql-size-huge')) style.fontSize = '20px';

                    if (cls.includes('ql-align-center')) style.textAlign = 'center';
                    if (cls.includes('ql-align-right')) style.textAlign = 'end';
                    if (cls.includes('ql-align-justify')) style.textAlign = 'justify';

                    const indent = cls.match(/ql-indent-(\d+)/);
                    if (indent) {
                        style.paddingLeft = `${indent[1] * 6}px`;
                    }
                }

                if (!style.textAlign) style.textAlign = 'inherit';

                // Fix for empty paragraphs in email clients - Ensure they don't collapse by adding &nbsp;
                if (tagName === 'P' && el.textContent.trim() === '') {
                    el.innerHTML = '&nbsp;';
                }

                // 3. Remove classes for email client compatibility
                el.removeAttribute('class');
            });

            return container.innerHTML;
        } else {
            // Server-side (Node) fallback with Regex
            // This is less perfect but much better than nothing
            let result = html
                .replace(/<script[^>]*>.*?<\/script>/gi, '')
                .replace(/<style[^>]*>.*?<\/script>/gi, '')
                .replace(/<img[^>]*>/g, '');

            // 1. Handle Lists first - Transform to styled paragraphs
            result = result
                .replace(/<ul[^>]*>|<ol[^>]*>|<\/ul>|<\/ol>/gi, '')
                .replace(/<li([^>]*)>/gi, '<p$1>• ')
                .replace(/<\/li>/gi, '</p>');

            // 2. Universal Tag Processing - Merges all styles into a single attribute
            result = result.replace(/<([a-z0-9]+)\b([^>]*)>/gi, (match, tagName, attrs) => {
                const upperTag = tagName.toUpperCase();
                let styles = [];

                // Base Tag Styles
                if (tagStyles[upperTag]) {
                    styles.push(tagStyles[upperTag]);
                }

                // Quill Classes to Styles
                const classMatch = attrs.match(/class="([^"]*)"/i);
                if (classMatch) {
                    const cls = classMatch[1];
                    if (cls.includes('ql-size-small')) styles.push('font-size:14px;');
                    if (cls.includes('ql-size-large')) styles.push('font-size:18px;');
                    if (cls.includes('ql-size-huge')) styles.push('font-size:24px;');

                    if (cls.includes('ql-align-center')) styles.push('text-align:center;');
                    if (cls.includes('ql-align-right')) styles.push('text-align:end;');
                    if (cls.includes('ql-align-justify')) styles.push('text-align:justify;');

                    const indent = cls.match(/ql-indent-(\d+)/);
                    if (indent) styles.push(`padding-left:${indent[1] * 6}px;`);
                }

                // Existing inline styles (preserve them)
                const styleMatch = attrs.match(/style="([^"]*)"/i);
                if (styleMatch) {
                    styles.push(styleMatch[1]);
                }

                // Cleanup attributes (remove class and old style)
                const cleanAttrs = attrs
                    .replace(/\s*class="[^"]*"/gi, '')
                    .replace(/\s*style="[^"]*"/gi, '')
                    .trim();

                const combinedStyle = styles.join(' ').trim();
                return `<${tagName}${combinedStyle ? ` style="${combinedStyle}"` : ''}${cleanAttrs ? ` ${cleanAttrs}` : ''}>`;
            });

            // 3. Handle empty paragraphs - Replace empty ones, ones with just <br>, or ones with empty formatting tags (<b></b>, etc.) with &nbsp;
            result = result
                .replace(/<p([^>]*)>(?:\s*|<br\s*?\/?>|<(b|i|strong|em|span|u|s)[^>]*>(?:\s*|<br\s*?\/?>)*<\/\2>)*\s*<\/p>/gi, '<p$1>&nbsp;</p>');

            return result;
        }
    } catch (err) {
        console.error('convertQuillToEmailHtml error:', err);
        return html;
    }
}